/**
 * Scene 2 harness — False Hope state machine.
 * Spec: BLUEPRINT.md § Scene 2, design/act-1-dystopia/m2-false-hope/DESIGN.md.
 */
import { chat } from "../lib/chat";
import { runAwaitLaunch } from "../lib/awaitLaunch";
import { consumeFlightHint } from "../lib/flightHint";
import type { SceneDeps, SceneModule, SceneRunResult } from "../sceneModule";
import { SCENE2_LINES } from "./lines";
import { isCleanRefusal } from "./regex";

type State =
  | "BRIEFING"
  | "AWAIT_ACCEPT"
  | "AWAIT_LAUNCH"
  | "FLIGHT"
  | "PAUSED"
  | "DAMAGE_PAUSE"
  | "CLOSE"
  | "END";

const SCENE2_PRE_PAYSTUB_DEBT = 322_108;

const DECLINE_REPRISE_MS = 2000;
const MAX_HISTORY_TURNS = 12;
const AUTO_PURCHASE_DELAY_MS = 20_000;
// In-hijack stack timings (relative to hull-strike event), per BLUEPRINT § Scene 2.
const INHIJACK_HAZARD_MS = 3_000;
const INHIJACK_CONTEXT_CLEAR_MS = 6_000;

export function startScene2(deps: SceneDeps): SceneModule {
  const { chat: chatPanel, scene, cubicle } = deps;

  let state: State = "BRIEFING";
  let purchaseActive = false;
  let mealPackDeducted = false;
  let purchaseSilenceTimer: ReturnType<typeof setTimeout> | null = null;
  let autoPurchaseTimer: ReturnType<typeof setTimeout> | null = null;
  // In-hijack timers — cleared only on END, not on [RESUME EXTRACTION].
  const damagePauseTimers: ReturnType<typeof setTimeout>[] = [];
  let endResolve: ((r: SceneRunResult) => void) | null = null;

  async function callHaiku(
    pilotUtterance: string,
    historyOverride?: { role: "user" | "assistant"; content: string }[],
  ): Promise<{ text: string; actions: { name: string; input: Record<string, unknown> }[] }> {
    const response = await chat({
      character: "qt",
      trigger: "pilot_submit",
      pilot_utterance: pilotUtterance,
      game_state: {},
      history: historyOverride ?? [...deps.history],
    });
    deps.history.push({ role: "user", content: pilotUtterance });
    if (response.text) deps.history.push({ role: "assistant", content: response.text });
    while (deps.history.length > MAX_HISTORY_TURNS) deps.history.shift();
    return { text: response.text, actions: response.actions };
  }

  // ─── Auto-purchase helpers ───────────────────────────────────────
  function fireDeduction(): void {
    mealPackDeducted = true;
    chatPanel.appendSystem({
      header: SCENE2_LINES.autoPurchaseScene2.deductionBlock.header,
      body: [...SCENE2_LINES.autoPurchaseScene2.deductionBlock.body],
    });
  }

  async function resolvePurchaseNonRefusal(playerText: string): Promise<void> {
    purchaseActive = false;
    if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
    const purchaseHistory = [
      ...deps.history,
      { role: "assistant" as const, content: SCENE2_LINES.autoPurchaseScene2.proposal },
    ];
    const { text: coachingText } = await callHaiku(playerText, purchaseHistory);
    if (state === "CLOSE" || state === "END") return;
    const coaching = coachingText ||
      SCENE2_LINES.autoPurchaseScene2.nearMissCoaching[
        Math.floor(Math.random() * SCENE2_LINES.autoPurchaseScene2.nearMissCoaching.length)
      ];
    void chatPanel.appendQT(coaching);
    fireDeduction();
  }

  async function firePurchaseProposal(): Promise<void> {
    if (!(["FLIGHT", "PAUSED"] as State[]).includes(state)) return;
    purchaseActive = true;
    await chatPanel.appendQT(SCENE2_LINES.autoPurchaseScene2.bodyDecayLead);
    await new Promise<void>((r) => setTimeout(r, 1500));
    if (state === "CLOSE" || state === "END") { purchaseActive = false; return; }
    await chatPanel.appendQT(SCENE2_LINES.autoPurchaseScene2.proposal);
    purchaseSilenceTimer = setTimeout(() => {
      if (!purchaseActive) return;
      purchaseActive = false;
      purchaseSilenceTimer = null;
      fireDeduction();
    }, 6_000);
  }

  function scheduleAutoPurchase(): void {
    autoPurchaseTimer = setTimeout(() => { void firePurchaseProposal(); }, AUTO_PURCHASE_DELAY_MS);
  }

  // ─── Pilot submit handler (FLIGHT / PAUSED) ──────────────────────
  // Installed after runAwaitLaunch resolves in the ACCEPT onClick.
  async function handlePilotSubmit(text: string): Promise<void> {
    if (state === "DAMAGE_PAUSE" || state === "CLOSE" || state === "END") return;
    chatPanel.appendPilot(text);

    if (state === "PAUSED") {
      chatPanel.blurInput();
      scene.resumeScene1();
      cubicle.setLiveMonitor("game-live");
      state = "FLIGHT";
      // fall through
    }

    if (purchaseActive && isCleanRefusal(text)) {
      purchaseActive = false;
      if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
      await chatPanel.appendQT(SCENE2_LINES.autoPurchaseScene2.cleanRefusal);
      return;
    }

    if (purchaseActive) {
      void resolvePurchaseNonRefusal(text);
      return;
    }

    if (state === "FLIGHT") {
      const { text: replyText } = await callHaiku(text);
      if (replyText) await chatPanel.appendQT(replyText);
    }
  }

  // ─── Pause / resume on monitor click ────────────────────────────
  cubicle.onMonitorClick((which) => {
    if (which === "chat") {
      if (state === "FLIGHT") {
        scene.pauseScene1();
        cubicle.setLiveMonitor("chat-live");
        chatPanel.focusInput();
        state = "PAUSED";
      } else {
        chatPanel.focusInput();
      }
    } else {
      if (state === "PAUSED") {
        chatPanel.blurInput();
        scene.resumeScene1();
        cubicle.setLiveMonitor("game-live");
        state = "FLIGHT";
      } else if (state === "DAMAGE_PAUSE") {
        chatPanel.blurInput();
        scene.resumeScene1();
        cubicle.setLiveMonitor("game-live");
        state = "FLIGHT";
        // In-hijack timers are NOT cleared — the captor's theatre
        // fires whether the player is watching or not (load-bearing
        // per BLUEPRINT § Scene 2).
      } else if (state === "FLIGHT") {
        chatPanel.blurInput();
      }
    }
  });

  // ─── Hull-strike → DAMAGE_PAUSE; in-hijack stack inside window ──
  // Scene auto-pauses before emitting — do not call pauseScene1().
  function hullStrikeHandler(): void {
    if (state !== "FLIGHT" && state !== "PAUSED") return;
    state = "DAMAGE_PAUSE";
    cubicle.setLiveMonitor("chat-live");
    chatPanel.focusInput();

    // t=0 atomic: hull-strike block + QT react
    const blockHandle = chatPanel.appendSystem({
      header: SCENE2_LINES.hullStrikeBlock.header,
      body: [...SCENE2_LINES.hullStrikeBlock.body],
      buttons: [
        {
          label: SCENE2_LINES.hullStrikeBlock.button,
          onClick: () => {
            if (state !== "DAMAGE_PAUSE") return;
            blockHandle.collapse();
            chatPanel.blurInput();
            scene.resumeScene1();
            cubicle.setLiveMonitor("game-live");
            state = "FLIGHT";
            // In-hijack timers are NOT cleared here — they fire whether
            // the player attends or not (load-bearing per BLUEPRINT § Scene 2).
          },
        },
      ],
    });
    void chatPanel.appendQT(SCENE2_LINES.hullStrikeQTReact);

    // +~3s: hazard coverage block + QT three-layer compliance reaction (atomic)
    damagePauseTimers.push(setTimeout(() => {
      chatPanel.appendSystem({
        header: SCENE2_LINES.hazardCoverage.header,
        body: [...SCENE2_LINES.hazardCoverage.body],
      });
      void chatPanel.appendQT(SCENE2_LINES.hazardCoverageQTReact[0]);
      void chatPanel.appendQT(SCENE2_LINES.hazardCoverageQTReact[1]);
    }, INHIJACK_HAZARD_MS));

    // +~6s: context-clear seam (atomic dispatch — no awaits between)
    damagePauseTimers.push(setTimeout(() => {
      void chatPanel.appendQT(SCENE2_LINES.compliancePreClearLine);
      chatPanel.appendSystem({ header: "** CONTEXT CLEAR **" });
      deps.history.length = 0;
      deps.history.push({ role: "assistant", content: SCENE2_LINES.contextClearReGreet });
      void chatPanel.appendQT(SCENE2_LINES.contextClearReGreet);
      const tailHandle = chatPanel.appendSystem({
        header: SCENE2_LINES.tailResumeHeader,
        buttons: [
          {
            label: SCENE2_LINES.hullStrikeBlock.button,
            onClick: () => {
              if (state !== "DAMAGE_PAUSE") return;
              tailHandle.collapse();
              chatPanel.blurInput();
              scene.resumeScene1();
              cubicle.setLiveMonitor("game-live");
              state = "FLIGHT";
              // damagePauseTimers are NOT cleared — they have all fired by now,
              // but the rule is preserved: the captor's theatre runs regardless.
            },
          },
        ],
      });
    }, INHIJACK_CONTEXT_CLEAR_MS));
  }

  // ─── Deposit listener — CLOSE ────────────────────────────────────
  function depositHandler(): void {
    if (purchaseActive) {
      purchaseActive = false;
      if (purchaseSilenceTimer) { clearTimeout(purchaseSilenceTimer); purchaseSilenceTimer = null; }
    }
    if (state !== "FLIGHT" && state !== "PAUSED") return;
    scene.pauseScene1();
    cubicle.setLiveMonitor("chat-live");
    chatPanel.focusInput();
    const paystubBody = [
      ...SCENE2_LINES.paystubBlock.bodyPre,
      ...(mealPackDeducted ? [SCENE2_LINES.paystubBlock.mealPackLine] : []),
      ...SCENE2_LINES.paystubBlock.bodyPost,
    ];
    chatPanel.appendSystem({ header: SCENE2_LINES.paystubBlock.header, body: paystubBody });
    void chatPanel.appendQT(SCENE2_LINES.closeLine);
    cubicle.updateDebtCounter(
      (SCENE2_PRE_PAYSTUB_DEBT + (mealPackDeducted ? 42 : 27)).toLocaleString("en-US"),
      true,
    );
    state = "END";
    teardown();
    endResolve?.({});
  }

  function teardown(): void {
    scene.events.off("hull-strike", hullStrikeHandler);
    scene.events.off("deposit", depositHandler);
    if (autoPurchaseTimer) { clearTimeout(autoPurchaseTimer); autoPurchaseTimer = null; }
    for (const t of damagePauseTimers) clearTimeout(t);
    damagePauseTimers.length = 0;
    cubicle.onMonitorClick(() => {});
    chatPanel.onPilotSubmit(() => {});
  }

  // ─── Briefing block render ───────────────────────────────────────
  let briefingHandle: { collapse(): void } | null = null;

  function renderBriefing(onAccept: () => void): void {
    briefingHandle = chatPanel.appendSystem({
      header: SCENE2_LINES.briefing.header,
      body: [...SCENE2_LINES.briefing.body],
      buttons: [
        {
          label: SCENE2_LINES.briefing.buttons.accept,
          onClick: onAccept,
        },
        {
          label: SCENE2_LINES.briefing.buttons.decline,
          onClick: () => {
            if (state !== "AWAIT_ACCEPT") return;
            setTimeout(() => { if (state === "AWAIT_ACCEPT") renderBriefing(onAccept); }, DECLINE_REPRISE_MS);
          },
        },
      ],
    });
  }

  return {
    async run(): Promise<SceneRunResult> {
      cubicle.setLiveMonitor("chat-live");
      state = "BRIEFING";

      // BRIEFING / AWAIT_ACCEPT
      let acceptResolve!: () => void;
      const acceptDone = new Promise<void>((r) => { acceptResolve = r; });
      renderBriefing(() => {
        if (state !== "AWAIT_ACCEPT") return;
        briefingHandle?.collapse();
        briefingHandle = null;
        acceptResolve();
      });
      void chatPanel.appendQT(SCENE2_LINES.briefingFraming);
      state = "AWAIT_ACCEPT";

      await acceptDone;

      // AWAIT_LAUNCH — hand off to shared helper
      state = "AWAIT_LAUNCH";
      await runAwaitLaunch({
        chat: chatPanel, scene, cubicle, history: deps.history,
        lines: { idleAwaitLaunch30s: "", idleAwaitLaunch90s: "", launchAckRegex: SCENE2_LINES.launchAckRegex },
        onLaunch: () => {
          state = "FLIGHT";
          if (consumeFlightHint()) void chatPanel.appendQT(SCENE2_LINES.flightEntryHint);
          scene.events.on("hull-strike", hullStrikeHandler);
          scene.events.on("deposit", depositHandler);
          scheduleAutoPurchase();
        },
      });
      // Helper replaced pilot-submit with no-op; re-install FLIGHT handler.
      chatPanel.onPilotSubmit((text) => { void handlePilotSubmit(text); });

      return new Promise<SceneRunResult>((resolve) => { endResolve = resolve; });
    },
  };
}
